import {
  COMMON_CONSTANTS,
  Loader,
  TEXT_LENGTH,
  withErrorBoundary
} from '@nile/components';
import { AccountClient, TicketClient } from '@nile/graphql';
import { GET_ALL_APPROVERS, SEND_APPROVAL } from '@nile/graphql';
import {
  Button,
  Page,
  ButtonGroup,
  ResourceList,
  TextStyle,
  ResourceItem,
  TextField,
  Card,
  Badge
} from '@superops/ui-design-system';
import React, { FC, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  getTranslatedText,
  toast,
  useCheckIsItProduct
} from '@nile/components/src/Utils/helpers';
import { cloneDeep, isEmpty, omit } from 'lodash';
import { SVGIcon } from '@superops/super-icons';
import { FormattedMessage, useIntl } from 'react-intl';

interface Client {
  name?: string;
  accountId?: string;
  logoFileName?: string | null;
  primaryContact?: string | null;
}

interface ApprovalListProps {
  client: Client;
  onSave?: () => void;
  onCancel?: () => void;
  ticketId: string;
}

const ApproverListSheet: FC<ApprovalListProps> = (props) => {
  const { ticketId, client, onSave, onCancel } = props;
  const isItProduct = useCheckIsItProduct();
  const intl = useIntl();

  const [requesterApprovers, setRequesterApprovers] = useState(null);
  const [technicianApprovers, setTechnicianApprovers] = useState(null);
  const [approvalMessage, setApprovalMessage] = useState('');

  const [selectedApprovers, setSelectedApprovers] = useState(null);

  const placeholder = getTranslatedText(intl, 'common.enter.your.message.here');

  const getProcessedList = ({ selection, approvers, type }) => {
    const selectedItemArr = [];
    const parentData = cloneDeep(approvers);
    selection.forEach((item) => {
      const selectedItemIndex = parentData.findIndex(
        (el) => el?.userId === item
      );
      if (selectedItemIndex > -1) {
        const selectedData = parentData[selectedItemIndex];
        if (selectedData) {
          selectedItemArr.push({
            ...selectedData,
            index: selectedItemIndex,
            type
          });
        }
        parentData.splice(selectedItemIndex, 1);
      }
    });

    const updatedSelction = selectedApprovers
      ? [...selectedApprovers, ...selectedItemArr]
      : selectedItemArr;
    setSelectedApprovers(updatedSelction);

    return { updatedParent: parentData, selectedItems: selectedItemArr };
  };

  const handleSelect = ({ type, selection }) => {
    if (type === 'TECHNICIAN') {
      const updatedTechnicianList = getProcessedList({
        type,
        selection: selection,
        approvers: technicianApprovers
      });

      const updatedTechList = updatedTechnicianList?.updatedParent;
      setTechnicianApprovers(updatedTechList);
    }

    if (type === 'REQUESTER') {
      const updatedRequesterList = getProcessedList({
        type,
        selection: selection,
        approvers: requesterApprovers
      });

      const updatedReqList = updatedRequesterList?.updatedParent;
      setRequesterApprovers(updatedReqList);
    }
  };

  const handleUnselect = (updatedItems) => {
    const updatedSelection = [];

    selectedApprovers.forEach((user) => {
      const updatedUser = omit(user, ['type', 'index']);
      const isSelected = updatedItems.find((itemId) => itemId === user?.userId);
      if (!isSelected) {
        if (user?.type === 'REQUESTER') {
          const updatedRequesterAppovers = cloneDeep(requesterApprovers);
          if (user?.index > -1) {
            updatedRequesterAppovers.splice(user?.index, 0, updatedUser);
          } else {
            updatedRequesterAppovers.push(updatedUser);
          }
          setRequesterApprovers(updatedRequesterAppovers);
        }

        if (user?.type === 'TECHNICIAN') {
          const updatedTechniainAppovers = cloneDeep(technicianApprovers);
          if (user?.index > -1) {
            updatedTechniainAppovers.splice(user?.index, 0, updatedUser);
          } else {
            updatedTechniainAppovers.push(updatedUser);
          }
          setTechnicianApprovers(updatedTechniainAppovers);
        }
      } else {
        updatedSelection.push(user);
      }
    });
    setSelectedApprovers(updatedSelection);
  };

  const LIST_INFO = {
    sort: [
      {
        attribute: 'name',
        order: 'ASC'
      }
    ]
  };

  const getMSPVariables = (name: String) => {
    switch (name) {
      case 'technician':
        return {
          roleType: 'TECHNICIAN',
          listInfo: LIST_INFO
        };
      case 'requester':
        return {
          roleType: 'REQUESTER',
          listInfo: LIST_INFO,
          client: {
            accountId: client?.accountId
          }
        };
      default:
        return null;
    }
  };

  const getITVariables = (name: String) => {
    switch (name) {
      case 'requester':
        return {
          roleType: 'REQUESTER',
          listInfo: LIST_INFO
        };
      default:
        return null;
    }
  };

  const {
    data: requesterApproversData,
    loading: requesterApproversLoading,
    error: requesterApproversError
  } = useQuery(GET_ALL_APPROVERS, {
    client: AccountClient,
    variables: {
      input: isItProduct
        ? getITVariables('requester')
        : getMSPVariables('requester')
    },
    fetchPolicy: 'no-cache',
    onCompleted(res) {
      if (res?.getTicketApproverList?.userList) {
        setRequesterApprovers(res?.getTicketApproverList?.userList);
      }
    }
  });

  const {
    data: technicianApproversData,
    loading: technicianApproversLoading,
    error: technicianApproversError
  } = useQuery(GET_ALL_APPROVERS, {
    client: AccountClient,
    skip: isItProduct,
    variables: {
      input: getMSPVariables('technician')
    },
    fetchPolicy: 'no-cache',
    onCompleted(res) {
      if (res?.getTicketApproverList?.userList) {
        setTechnicianApprovers(res?.getTicketApproverList?.userList);
      }
    }
  });

  const [sendApproval, { loading: sendApprovalLoading }] = useMutation(
    SEND_APPROVAL,
    {
      client: TicketClient,
      onCompleted(res) {
        if (res?.sendApproval) {
          if (onSave) {
            toast('success', 'ticket.runbooks.approvalSent.success');
            onSave(res?.sendApproval);
          }
        }
      }
    }
  );

  const handleSave = () => {
    if (!isEmpty(selectedApprovers)) {
      const requesterInput = [];
      const technicianInput = [];

      selectedApprovers.forEach((user) => {
        const userData = user?.userId ? { userId: user?.userId } : null;
        if (userData) {
          if (user?.type === 'TECHNICIAN') {
            technicianInput.push(userData);
          } else {
            requesterInput.push(userData);
          }
        }
      });

      sendApproval({
        variables: {
          input: {
            ticket: {
              ticketId: ticketId
            },
            requester: requesterInput,
            technician: technicianInput,
            additionalMessage: approvalMessage || null
          }
        }
      });
    }
  };

  const isLoading = technicianApproversLoading || requesterApproversLoading;
  return (
    <div id="approverListSheet" className="vw30 fhp">
      <Page
        title={
          <FormattedMessage id="ticket.details.insights.initiateApprovals" />
        }
        contentHeight="150px"
        primaryAction={
          <ButtonGroup>
            <Button plainSecondary onClick={onCancel || null}>
              <FormattedMessage id="common.cancel" />
            </Button>
            <Button
              primary
              onClick={handleSave}
              loading={sendApprovalLoading}
              icon={<SVGIcon name="SendWhite" />}
              disabled={isEmpty(selectedApprovers)}
            >
              <FormattedMessage id="common.send" />
            </Button>
          </ButtonGroup>
        }
        variant="secondary"
      >
        {isLoading ? (
          <div className="flex actr jctr fhp w100">
            <Loader />
          </div>
        ) : (
          <>
            <div id="list-wrapper" className="pl20 pr-20">
              <div className="mb20">
                <Card noShadow padding="sizeSmall" variant="secondaryCard">
                  <div className="mb5">
                    <FormattedMessage id="ticket.approvers.message.add.label" />
                  </div>

                  <div>
                    <TextField
                      multiline
                      placeholder={placeholder}
                      onChange={(val) => setApprovalMessage(val)}
                      value={approvalMessage}
                      bgTransparent
                      maxLength={TEXT_LENGTH.size_5}
                      maxHeight={80}
                    />
                  </div>
                </Card>
              </div>

              {!isEmpty(selectedApprovers) && (
                <>
                  <div className="pl20 mb10">
                    <TextStyle variation="subdued" weight="SemiBold">
                      <div className="fsize12">
                        <FormattedMessage id="ticket.selected.approvers" />
                      </div>
                    </TextStyle>
                  </div>

                  <ResourceList
                    onSelectionChange={handleUnselect}
                    selectedItems={
                      selectedApprovers
                        ? selectedApprovers.map((el) => el?.userId)
                        : []
                    }
                    items={selectedApprovers || []}
                    renderItem={(item) => {
                      const { userId, name } = item;
                      const BADGE_TEXT_MAP = {
                        TECHNICIAN: 'entity.technician',
                        REQUESTER: 'entity.requester'
                      };
                      const badgeText = item?.type
                        ? BADGE_TEXT_MAP?.[item?.type]
                        : null;
                      return (
                        <div className="white-resource-item-bg">
                          <ResourceItem id={userId}>
                            <div className="flex g10 ">
                              <div>
                                <TextStyle weight="Medium">{name}</TextStyle>
                              </div>
                              <Badge>
                                <FormattedMessage id={badgeText} />
                              </Badge>
                            </div>
                          </ResourceItem>
                        </div>
                      );
                    }}
                    selectable
                    hideSelectAllOption
                  />
                </>
              )}

              {!isEmpty(technicianApprovers) && (
                <>
                  <div className="pl20 mb10">
                    <TextStyle variation="subdued" weight="SemiBold">
                      <div className="fsize12">
                        <FormattedMessage id="entity.technician" />
                      </div>
                    </TextStyle>
                  </div>

                  <ResourceList
                    onSelectionChange={(item) => {
                      handleSelect({ type: 'TECHNICIAN', selection: item });
                    }}
                    items={technicianApprovers || []}
                    renderItem={(item) => {
                      const { userId, name } = item;
                      return (
                        <ResourceItem id={userId}>
                          <TextStyle weight="Medium">{name}</TextStyle>
                        </ResourceItem>
                      );
                    }}
                    selectable
                    hideSelectAllOption
                  />
                </>
              )}

              {!isEmpty(requesterApprovers) && (
                <>
                  <div className="pl20 mb10 mt15">
                    <TextStyle variation="subdued" weight="SemiBold">
                      <div className="fsize12">
                        <FormattedMessage id="entity.requester" />
                      </div>
                    </TextStyle>
                  </div>

                  <ResourceList
                    onSelectionChange={(item) => {
                      handleSelect({ type: 'REQUESTER', selection: item });
                    }}
                    items={requesterApprovers || []}
                    renderItem={(item) => {
                      const { userId, name } = item;
                      return (
                        <ResourceItem id={userId}>
                          <TextStyle weight="Medium">{name}</TextStyle>
                        </ResourceItem>
                      );
                    }}
                    selectable
                    hideSelectAllOption
                  />
                </>
              )}
            </div>
          </>
        )}
      </Page>
    </div>
  );
};

export default withErrorBoundary(ApproverListSheet);
